import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useFirebase } from '../contexts/FirebaseContext'
import { 
  BookOpen, 
  Plus, 
  Search, 
  Filter, 
  Edit, 
  Trash2, 
  Eye,
  FileText,
  X,
  Calendar,
  Target,
  AlertTriangle,
  CheckCircle
} from 'lucide-react'
import { SkeletonTable } from '../components/Skeleton'
import toast from 'react-hot-toast'

const NursingBullets = () => {
  const [bullets, setBullets] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [editModal, setEditModal] = useState({ isOpen: false, bullet: null })
  const [viewModal, setViewModal] = useState({ isOpen: false, bullet: null })
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, bullet: null })
  const [editTitle, setEditTitle] = useState('')
  const [editLoading, setEditLoading] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const { getNursingBullets, deleteBullet, updateNursingBullet } = useFirebase()

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const bulletsData = await getNursingBullets()
      setBullets(bulletsData || [])
    } catch (error) {
      console.error('Error loading data:', error)
      toast.error('Failed to load nursing bullets')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = (bullet) => {
    setDeleteModal({ isOpen: true, bullet })
  }

  const confirmDelete = async () => {
    if (!deleteModal.bullet) return
    try {
      setDeleteLoading(true)
      await deleteBullet(deleteModal.bullet.id)
      setBullets(prev => prev.filter(b => b.id !== deleteModal.bullet.id))
      toast.success('Nursing bullet deleted successfully')
      setDeleteModal({ isOpen: false, bullet: null })
    } catch (error) {
      console.error('Error deleting bullet:', error)
      toast.error('Failed to delete nursing bullet')
    } finally {
      setDeleteLoading(false)
    }
  }

  const cancelDelete = () => {
    setDeleteModal({ isOpen: false, bullet: null })
    setDeleteLoading(false)
  }

  const openViewModal = (bullet) => {
    setViewModal({ isOpen: true, bullet })
  }

  const closeViewModal = () => {
    setViewModal({ isOpen: false, bullet: null })
  }

  const openEditModal = (bullet) => {
    setEditModal({ isOpen: true, bullet })
    setEditTitle(bullet.title || '')
  }

  // Handle keyboard shortcuts for modals
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (editModal.isOpen) closeEditModal()
        if (viewModal.isOpen) closeViewModal()
        if (deleteModal.isOpen) cancelDelete()
      } else if (event.key === 'Enter' && editModal.isOpen && !editLoading) {
        event.preventDefault()
        handleUpdateTitle()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [editModal.isOpen, viewModal.isOpen, deleteModal.isOpen, editLoading])

  const closeEditModal = () => {
    setEditModal({ isOpen: false, bullet: null })
    setEditTitle('')
    setEditLoading(false)
  }

  const handleUpdateTitle = async () => {
    if (!editTitle.trim()) {
      toast.error('Title cannot be empty')
      return
    }

    try {
      setEditLoading(true)
      const updatedBullet = {
        ...editModal.bullet,
        title: editTitle.trim(),
        updatedAt: Date.now()
      }

      await updateNursingBullet(editModal.bullet.id, updatedBullet)
      
      // Update local state
      setBullets(bullets.map(bullet => 
        bullet.id === editModal.bullet.id 
          ? { ...bullet, title: editTitle.trim(), updatedAt: Date.now() }
          : bullet
      ))
      
      toast.success(`Title updated successfully to "${editTitle.trim()}"`)
      closeEditModal()
    } catch (error) {
      console.error('Error updating title:', error)
      toast.error('Failed to update title. Please try again.')
    } finally {
      setEditLoading(false)
    }
  }

  const filteredBullets = bullets.filter(bullet => {
    const matchesSearch = bullet.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         bullet.content?.toLowerCase().includes(searchTerm.toLowerCase())
    return matchesSearch
  })

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl shadow-xl p-8 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2 flex items-center">
              <BookOpen className="h-8 w-8 mr-3" />
              Nursing Bullets Management
            </h1>
            <p className="text-blue-100">
              Manage comprehensive lesson-wise nursing content and study materials
            </p>
          </div>
          <Link to="/nursing-bullets/add" className="btn-white">
            <Plus className="h-5 w-5 mr-2" />
            Add New Bullet
          </Link>
        </div>
      </div>


      {/* Search */}
      <div className="card p-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <input
            type="text"
            placeholder="Search bullets by title or content..."
            className="input pl-10 w-full"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Bullets List */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900">
            Nursing Bullets ({filteredBullets.length})
          </h3>
        </div>
        <div className="card-body p-0">
          {loading ? (
            <SkeletonTable rows={8} columns={5} />
          ) : filteredBullets.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Title
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Bullets Count
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Add Bullets
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredBullets.map((bullet) => {
                    return (
                      <tr key={bullet.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="flex items-start space-x-3">
                            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                              <BookOpen className="h-5 w-5 text-blue-600" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <h4 className="text-sm font-medium text-gray-900 truncate">
                                {bullet.title}
                              </h4>
                              <p className="text-sm text-gray-500 truncate max-w-xs">
                                {bullet.description || bullet.content?.substring(0, 100)}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-left">
                          <div className="flex items-center justify-center text-sm text-gray-600">
                            <FileText className="h-4 w-4 mr-1" />
                            <span className="font-medium">{bullet.bulletCount || 0}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-left text-sm text-gray-600">
                          {bullet.createdAt ? 
                            new Date(bullet.createdAt).toLocaleDateString('en-GB', {
                              day: '2-digit',
                              month: '2-digit', 
                              year: 'numeric'
                            }) 
                            : 'N/A'
                          }
                        </td>
                        <td className="px-6 py-4 text-left">
                          <Link
                            to={`/nursing-bullets/add-points/${bullet.id}`}
                            className="inline-flex items-center justify-center w-10 h-10 bg-blue-100 hover:bg-blue-200 text-blue-600 hover:text-blue-700 rounded-full transition-all duration-200 hover:shadow-md"
                            title="Add Bullets"
                          >
                            <Plus className="h-5 w-5" />
                          </Link>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-center space-x-2">
                            <button
                              onClick={() => openViewModal(bullet)}
                              className="text-blue-600 hover:text-blue-900 p-2 hover:bg-blue-50 rounded-full transition-all duration-200"
                              title="View Details"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            <Link
                              to={`/nursing-bullets/edit/${bullet.id}`}
                              className="text-green-600 hover:text-green-900 p-2 hover:bg-green-50 rounded-full transition-all duration-200"
                              title="Edit Bullet & Points"
                            >
                              <Edit className="h-4 w-4" />
                            </Link>
                            <button
                              onClick={() => handleDelete(bullet)}
                              className="text-red-600 hover:text-red-900 p-2 hover:bg-red-50 rounded-full transition-all duration-200"
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No nursing bullets found</h3>
              <p className="text-gray-600 mb-4">
                {searchTerm 
                  ? 'Try adjusting your search criteria'
                  : 'Get started by creating your first nursing bullet'
                }
              </p>
              {!searchTerm && (
                <Link to="/nursing-bullets/add" className="btn-primary">
                  <Plus className="h-5 w-5 mr-2" />
                  Add First Bullet
                </Link>
              )}
            </div>
          )}
        </div>
      </div>

      {/* View Modal */}
      {viewModal.isOpen && viewModal.bullet && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 rounded-t-lg">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-white flex items-center">
                  <Eye className="h-6 w-6 mr-2" />
                  Nursing Bullet Details
                </h3>
                <button
                  onClick={closeViewModal}
                  className="text-white hover:text-gray-200 p-1 hover:bg-white/20 rounded transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-6">
              {/* Title Section */}
              <div className="bg-blue-50 rounded-lg p-4">
                <h4 className="text-lg font-semibold text-gray-900 flex items-center mb-2">
                  <BookOpen className="h-5 w-5 mr-2 text-blue-600" />
                  {viewModal.bullet.title}
                </h4>
                {viewModal.bullet.description && (
                  <p className="text-gray-700 text-sm">{viewModal.bullet.description}</p>
                )}
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Title</label>
                    <div className="flex items-center text-gray-900">
                      <Target className="h-4 w-4 mr-2 text-gray-400" />
                      <span>{viewModal.bullet.title}</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Bullet Count</label>
                    <div className="flex items-center text-gray-900">
                      <FileText className="h-4 w-4 mr-2 text-blue-500" />
                      <span className="text-xl font-bold text-blue-600">
                        {viewModal.bullet.bulletPoints?.length || viewModal.bullet.bulletCount || 0}
                      </span>
                      <span className="text-sm text-gray-500 ml-2">points</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Created Date</label>
                    <div className="flex items-center text-gray-900">
                      <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                      <span>
                        {viewModal.bullet.createdAt ? 
                          new Date(viewModal.bullet.createdAt).toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: '2-digit', 
                            year: 'numeric'
                          }) 
                          : 'N/A'
                        }
                      </span>
                    </div>
                  </div>

                  
                </div>
              </div>

              {/* Content Section */}
              {viewModal.bullet.content && (
                <div className="border-t border-gray-200 pt-6">
                  <label className="block text-sm font-medium text-gray-500 mb-3">Content</label>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-gray-900 leading-relaxed whitespace-pre-wrap">
                      {viewModal.bullet.content}
                    </p>
                  </div>
                </div>
              )}

              {/* Bullet Points Preview */}
              {viewModal.bullet.bulletPoints && viewModal.bullet.bulletPoints.length > 0 && (
                <div className="border-t border-gray-200 pt-6">
                  <label className="block text-sm font-medium text-gray-500 mb-3">
                    Bullet Points ({viewModal.bullet.bulletPoints.length})
                  </label>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {viewModal.bullet.bulletPoints.slice(0, 5).map((point, index) => (
                      <div key={index} className="flex items-start space-x-2 p-2 bg-gray-50 rounded">
                        <span className="inline-flex items-center justify-center w-5 h-5 bg-blue-100 text-blue-600 rounded-full text-xs font-medium flex-shrink-0 mt-0.5">
                          {index + 1}
                        </span>
                        <p className="text-sm text-gray-700 leading-relaxed">{point}</p>
                      </div>
                    ))}
                    {viewModal.bullet.bulletPoints.length > 5 && (
                      <div className="text-center text-sm text-gray-500 py-2">
                        ... and {viewModal.bullet.bulletPoints.length - 5} more points
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-4 rounded-b-lg flex justify-between">
              <Link
                to={`/nursing-bullets/add-points/${viewModal.bullet.id}`}
                className="btn-primary"
                onClick={closeViewModal}
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit Points
              </Link>
              <button
                onClick={closeViewModal}
                className="btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Edit Modal */}
      {editModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-green-600 to-green-700 px-6 py-4 rounded-t-lg">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-white flex items-center">
                  <Edit className="h-5 w-5 mr-2" />
                  Edit Nursing Bullet
                </h3>
                <button
                  onClick={closeEditModal}
                  className="text-white hover:text-gray-200 p-1 hover:bg-white/20 rounded transition-colors"
                  disabled={editLoading}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            
            {/* Modal Body */}
            <div className="p-6 space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <div className="flex items-center">
                  <AlertTriangle className="h-4 w-4 text-yellow-600 mr-2" />
                  <span className="text-sm text-yellow-800">
                    You are editing: <strong>{editModal.bullet?.title}</strong>
                  </span>
                </div>
              </div>

              <div>
                <label htmlFor="editTitle" className="block text-sm font-medium text-gray-700 mb-2">
                  New Title *
                </label>
                <input
                  id="editTitle"
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Enter new bullet title..."
                  disabled={editLoading}
                  autoFocus
                />
              </div>
              
              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  onClick={closeEditModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                  disabled={editLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateTitle}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  disabled={editLoading || !editTitle.trim()}
                >
                  {editLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Updating...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Update Title
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal.isOpen && deleteModal.bullet && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Delete Nursing Bullet
              </h3>
              <p className="text-gray-600 mb-6">
                Are you sure you want to delete "<strong>{deleteModal.bullet.title}</strong>"? 
                This action cannot be undone.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={cancelDelete}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md transition-colors"
                  disabled={deleteLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors disabled:opacity-50 flex items-center"
                  disabled={deleteLoading}
                >
                  {deleteLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Deleting...
                    </>
                  ) : (
                    'Delete'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default NursingBullets
